import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataState } from '../../context/DataProvider';
import { API_URL } from '../../services/api';

// Helper function to escape a string to be safely embedded
const BACKEND_API_PROXY_URL = `${API_URL}/external-data-proxy`;

// within a JavaScript single-quoted string literal.
function escapeJsStringForEmbedding(str) {
    if (typeof str !== 'string') {
        // In case something other than a string is accidentally passed
        // though toolReferences should always provide strings.
        return String(str);
    }
    return str
        .replace(/\\/g, '\\\\')  // 1. Escape backslashes FIRST
        .replace(/'/g, '\\\'')   // 2. Escape single quotes (since we'll use single quotes for JSON.parse('...'))
        .replace(/\n/g, '\\n')   // Escape newlines
        .replace(/\r/g, '\\r')   // Escape carriage returns
        .replace(/\t/g, '\\t')   // Escape tabs
        .replace(/\u2028/g, '\\u2028') // Line separator
        .replace(/\u2029/g, '\\u2029'); // Paragraph separator
}

// Component 1: StyledModal - The modal window for dynamic content with Zinc theme
const StyledModal = ({ isOpen, onClose, title, children, error, isLoading }) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm transition-opacity duration-300 ease-in-out" // Changed bg-slate-900 to bg-black for deeper backdrop
      onClick={onClose} // Close on backdrop click
    >
      <div 
        className="bg-zinc-900 text-zinc-200 rounded-xl shadow-2xl w-full max-w-10xl max-h-[95vh] flex flex-col overflow-hidden border border-zinc-700 transform transition-all duration-300 ease-in-out" // max-w-3xl to max-w-4xl, themed with zinc
        onClick={(e) => e.stopPropagation()} // Prevent modal close when clicking inside modal content
      >

        <header className="p-5 border-b border-zinc-800 flex justify-between items-center flex-shrink-0">
          <h2 className="text-xl font-semibold text-zinc-100">{title || "Dynamic Content"}</h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-100 transition-colors p-1.5 rounded-full hover:bg-zinc-700" // Themed button
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>
        
        <main className="p-6 overflow-y-auto flex-grow bg-zinc-900/50"> {/* Added subtle bg to main */}
          {isLoading ? (
             <div className="flex items-center justify-center h-full min-h-[200px]"> {/* Ensured loading takes space */}
                <p className="text-lg text-blue-400 animate-pulse">Processing component...</p> {/* Kept blue for loading status */}
             </div>
          ) : error ? (
            <div className="text-red-400 bg-red-900/20 p-4 rounded-md border border-red-700/50"> {/* Themed error message */}
              <p className="font-semibold text-red-300">Error Rendering Component:</p>
              <pre className="text-xs whitespace-pre-wrap mt-2 text-red-400 break-all">{error}</pre>
            </div>
          ) : (
            children
          )}
        </main>
        <footer className="p-3 bg-zinc-900 border-t border-zinc-800 text-center flex-shrink-0"> {/* Themed footer */}
          <p className="text-xs text-zinc-500">
            Dynamically Rendered React Component
          </p>
        </footer>
      </div>
    </div>
  );
};

// Component 2: InteractiveStripWithModal - Manages strip, modal state, and component processing with Zinc theme
const DynamicReactComponentStrip = ({ componentString, toolReferences, stripTitle, modalTitle }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [RenderableComponent, setRenderableComponent] = useState(null);
  const [processingError, setProcessingError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { userGoogleId: applicationAuthToken } = DataState(); // Adjust how you get this token

  const openModal = useCallback(() => {
    setIsModalOpen(true);
    setRenderableComponent(null);
    setProcessingError(null);
    setIsLoading(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

    // This function will be passed to the dynamic component's scope
  // It allows the dynamic component to get the current application auth token
  const getApplicationAuthToken = useCallback(() => {
    // You might need a more robust way to get the latest token if it can expire
    // and be refreshed while the modal is open.
    return applicationAuthToken;
  }, [applicationAuthToken]);

  useEffect(() => {
    if (!isModalOpen || !componentString) {
      if (!componentString && isModalOpen) {
        setProcessingError("No component string provided to render.");
        setIsLoading(false);
      }
      return;
    }
    
    setIsLoading(true); 

    // Ensure Babel is loaded from window scope
    if (typeof window.Babel === 'undefined' || typeof window.Babel.transform !== 'function') { // More robust check
      console.error("Babel Standalone is not loaded or Babel.transform is not a function.");
      setProcessingError("Critical Error: Babel Standalone script not found or invalid.");
      setIsLoading(false);
      return;
    }

    let processedString = componentString;

    // Step 1: Replace tool response placeholders
    try {
      if (toolReferences && typeof toolReferences === 'object') {
        const placeholderPattern = /\$\{tool_responses\.([a-zA-Z0-9_]+)\}/g;
        processedString = componentString.replace(placeholderPattern, (match, toolResponseId) => {
        //   if (toolReferences.hasOwnProperty(toolResponseId)) {
        //     const jsonDataString = toolReferences[toolResponseId];
        //     // Using template literals and escaping backticks within the jsonDataString for safety
        //     return `JSON.parse(\`${jsonDataString.replace(/`/g, '\\`')}\`)`;
        //   }
          if (toolReferences.hasOwnProperty(toolResponseId)) {
            const jsonDataString = toolReferences[toolResponseId]; // This is already a string of JSON
            // Ensure this jsonDataString is properly escaped to be embedded
            // within a JavaScript single-quoted string literal for JSON.parse('...')
            const escapedJsonDataString = escapeJsStringForEmbedding(jsonDataString);
            return `JSON.parse('${escapedJsonDataString}')`; // Using single quotes for the JS string literal
        }
          console.warn(`Placeholder ${match} found, but ID "${toolResponseId}" not in toolReferences.`);
          return 'undefined /* Placeholder not found */';
        });
      }
    } catch (e) {
      console.error("Error replacing tool_response placeholders:", e);
      setProcessingError(`Error during placeholder replacement: ${e.message}`);
      setIsLoading(false);
      return;
    }
    
    // Step 2: Transpile JSX using Babel Standalone from window scope
    let transpiledCode;
    try {
      const babelResult = window.Babel.transform(processedString, {
        presets: ['react'],
        comments: false,
      });
      transpiledCode = babelResult.code;
      if (transpiledCode.endsWith(';')) {
        transpiledCode = transpiledCode.slice(0, -1);
      }
    } catch (e) {
      console.error("Error transpiling component string with Babel:", e);
      setProcessingError(`Babel Transpilation Error: ${e.message}`);
      setIsLoading(false);
      return;
    }

    // Step 3: Create the component function using new Function
    try {
      const componentFactory = new Function(
        'React', 'useState', 'useEffect', 'useMemo', 'useCallback', 'useRef', 
        'BACKEND_API_PROXY_URL', // Provide the proxy URL
        'getApplicationAuthToken', // Provide the function to get the app's auth token
        `return (${transpiledCode});`
      );
      const ActualComponent = componentFactory(
        React, React.useState, React.useEffect, React.useMemo, React.useCallback, React.useRef,
        BACKEND_API_PROXY_URL,
        getApplicationAuthToken
      );
      setRenderableComponent(() => ActualComponent);
      setProcessingError(null); 
    } catch (e) {
      console.error("Error creating component function with new Function():", e);
      setProcessingError(`Component Instantiation Error: ${e.message}.`);
    } finally {
      setIsLoading(false);
    }

  }, [isModalOpen, componentString, toolReferences]);

  return (
    <>
      {/* Clickable Strip - Themed with Zinc */}
      <button
        onClick={openModal}
        className="w-full max-w-10xl mr-6 mx-auto p-2 bg-zinc-800 hover:bg-zinc-700/80 text-zinc-100 rounded-lg shadow-lg flex justify-between items-center transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:ring-offset-2 focus:ring-offset-zinc-900" // Themed strip
      >
        <span className="font-medium text-sm">{stripTitle || "View Dynamic Content"}</span> {/* text-xs to text-sm */}
        {/* Using a different icon for variety, can be changed back */}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </button>

      {/* Modal for rendering the dynamic component */}
      <StyledModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={modalTitle || stripTitle || "Dynamic Content"}
        isLoading={isLoading}
        error={processingError}
      >
        {RenderableComponent && !processingError && !isLoading && <RenderableComponent />}
      </StyledModal>
    </>
  );
};

export default DynamicReactComponentStrip;